import { useGetSafeClientCode } from '@/hooks/router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { Columns2Icon } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useCreateClientMarketTemplate } from '@/gen/publicApi'

import { Button } from '@/components/button'
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/dialog'
import { DropdownMenuItem } from '@/components/dropdown-menu'
import { Form } from '@/components/form'
import FormCheckbox from '@/components/form/formCheckbox'
import FormInput from '@/components/form/formInput'
import OptionMenu from '@/components/optionMenu'
import { useToast } from '@/components/toast/useToast'

interface Props {
    marketCode: string
}

const createTemplateFormSchema = z.object({
    newMarketName: z.string(),
    newMarketCode: z.string(),
    createDepth: z.boolean().optional(),
})

type CreateTemplateFormValues = z.infer<typeof createTemplateFormSchema>

export default function CreateMarketTemplateOption({ marketCode }: Props) {
    const [open, setOpen] = useState(false)
    const clientCode = useGetSafeClientCode()
    const { toast } = useToast()
    const navigate = useNavigate()
    const createTemplateMutator = useCreateClientMarketTemplate(clientCode, {
        mutation: {
            onSuccess: ({ code }) => {
                navigate({
                    to: `/$client_code/markets/$market_code`,
                    params: {
                        client_code: clientCode,
                        market_code: code,
                    },
                })
                toast({
                    title: 'New template created!',
                })
                setOpen(false)
            },
            onError: error => {
                console.error('Error creating template from market', error)
                toast({
                    variant: 'destructive',
                    title: 'Error creating templateet',
                })
            },
        },
    })
    const form = useForm<CreateTemplateFormValues>({
        resolver: zodResolver(createTemplateFormSchema),
        defaultValues: {
            createDepth: true,
        },
        mode: 'onChange',
    })
    const onSubmit = (data: CreateTemplateFormValues) => {
        createTemplateMutator.mutate({
            name: data.newMarketName,
            marketCode: data.newMarketCode,
            createDepth: data.createDepth ?? false,
            originMarketCode: marketCode,
        })
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <OptionMenu>
                    <DropdownMenuItem className="flex" onClick={() => setOpen(true)}>
                        <Columns2Icon className="w-4 h-4 mr-2" /> Create as Template
                    </DropdownMenuItem>
                </OptionMenu>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create Template from Market</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormInput control={form.control} name="newMarketName" label="Template Market Name" />
                        <FormInput control={form.control} name="newMarketCode" label="Template Market Code" />
                        <div className="flex flex-row items-center space-x-2">
                            <FormCheckbox control={form.control} name="createDepth" label="Create Depth" />
                        </div>

                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="outline" disabled={createTemplateMutator.isPending}>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button type="submit" disabled={!form.formState.isValid || createTemplateMutator.isPending}>
                                Create
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
